import React, { useState, useEffect } from "react";
import { db, auth } from "../firebaseConfig";
import { doc, getDoc, collection, addDoc, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
  Button,
  Row,
  Col,
  Card,
  Form,
  ProgressBar,
  Container,
  Alert,
  FormControl,
} from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import Header from "../layouts/Header";

export default function YearlyDocuments() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("");
  const [permissions, setPermissions] = useState(null);
  const [files, setFiles] = useState([]);
  const [file, setFile] = useState(null);
  const [createdForEmail, setCreatedForEmail] = useState("");
  const [uploadYear, setUploadYear] = useState(new Date().getFullYear());
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchUserData = async (authUser) => {
    if (authUser) {
      const userDocRef = doc(db, "Users", authUser.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setRole(userData.role || "user");
        return userData.role;
      } else {
        console.error("User document does not exist.");
        return null;
      }
    }
    return null;
  };

  const fetchPermissions = async (role) => {
    try {
      if (role === "admin") {
        setPermissions({ canViewYearlyDocuments: true });
        return;
      }

      const roleDocRef = doc(db, "Roles", role);
      const roleDoc = await getDoc(roleDocRef);

      if (roleDoc.exists()) {
        setPermissions(roleDoc.data().permissions);
      } else {
        console.error(`Role document does not exist for role: ${role}`);
        setPermissions(null);
      }
    } catch (err) {
      console.error("Error fetching permissions:", err);
      setPermissions(null);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, "Users"));
      const userList = usersSnapshot.docs.map((doc) => ({
        email: doc.data().email,
        fullName: doc.data().fullName || "Unknown User",
      }));
      setUsers(userList);
      setFilteredUsers(userList);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file || !createdForEmail || !uploadYear) {
      alert("Please complete all fields.");
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", "documents");

      const xhr = new XMLHttpRequest();
      xhr.open("POST", "https://api.cloudinary.com/v1_1/your-cloud-name/upload");

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(progress);
        }
      };

      xhr.onload = async () => {
        const data = JSON.parse(xhr.responseText);
        const fileRef = collection(db, "YearlyDocuments");
        await addDoc(fileRef, {
          fileName: file.name,
          url: data.secure_url,
          size: file.size,
          type: file.type,
          year: uploadYear,
          uploadedAt: new Date(),
          createdBy: { email: user.email, fullName: user.displayName || "Unknown" },
          createdFor: createdForEmail,
        });

        setUploading(false);
        setUploadProgress(0);
        setFile(null);
        setCreatedForEmail("");
        alert("Document uploaded successfully!");
        fetchFiles();
      };

      xhr.onerror = () => {
        setUploading(false);
        setUploadProgress(0);
        alert("Failed to upload document.");
      };

      xhr.send(formData);
    } catch (err) {
      console.error(err);
      alert("Failed to upload document.");
    }
  };

  const fetchFiles = async () => {
    try {
      const fileSnapshot = await getDocs(collection(db, "YearlyDocuments"));
      const allFiles = fileSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      let filteredFiles = [];
      if (role === "admin") {
        filteredFiles = selectedUser
          ? allFiles.filter(
              (file) =>
                file.createdBy.email === selectedUser.email ||
                file.createdFor === selectedUser.email
            )
          : allFiles;
      } else {
        filteredFiles = allFiles.filter(
          (file) => file.createdBy.email === user.email || file.createdFor === user.email
        );
      }

      setFiles(filteredFiles);
    } catch (err) {
      console.error(err);
      alert("Failed to fetch files.");
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    const lowerCaseQuery = query.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.fullName.toLowerCase().includes(lowerCaseQuery) ||
        user.email.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredUsers(filtered);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
      if (authUser) {
        setUser(authUser);
        const userRole = await fetchUserData(authUser);
        if (userRole) await fetchPermissions(userRole);
      } else {
        setUser(null);
        setRole("");
        setPermissions(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (permissions?.canViewYearlyDocuments || role === "admin") {
      fetchFiles();
    }
  }, [permissions, role, selectedUser]);

  useEffect(() => {
    if (role === "admin") {
      fetchAllUsers();
    }
  }, [role]);

  if (!permissions?.canViewYearlyDocuments && role !== "admin") {

    console.log("permissions" + permissions);
    return (
      <Container>
        <Alert variant="danger" className="text-center mt-5">
          Loading Content or Access Denied..
        </Alert>
      </Container>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <Container fluid className="main main-file-manager p-4">
        <Alert variant="info" className="mb-4">
          {role === "admin"
            ? "You are logged in as an admin and can manage documents for all employees."
            : `You are logged in as an employee. You can view your documents.`}
        </Alert>

        {role === "admin" && (
          <Card className="filter-card mb-4 shadow-sm">
            <Card.Body>
              <h5 className="mb-3 text-primary">Filter Documents by Employee</h5>
              <FormControl
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="mb-3"
              />
              <PerfectScrollbar style={{ maxHeight: "200px", overflow: "auto" }}>
                <ul className="list-group">
                  {filteredUsers.map((user, index) => (
                    <li
                      key={index}
                      className={`list-group-item ${selectedUser === user ? "active" : ""}`}
                      onClick={() => setSelectedUser(user)}
                      style={{ cursor: "pointer" }}
                    >
                      {user.fullName} ({user.email})
                    </li>
                  ))}
                </ul>
              </PerfectScrollbar>
            </Card.Body>
          </Card>
        )}

        {role === "admin" && (
          <Card className="upload-card mb-4 shadow-sm">
            <Card.Body>
              <h4 className="mb-3 text-primary">Upload Document</h4>
              <Form.Group controlId="fileUpload">
                <Form.Control
                  type="file"
                  onChange={handleFileChange}
                  className="mb-2"
                  placeholder="Select a document"
                />
                <Form.Control
                  type="email"
                  value={createdForEmail}
                  onChange={(e) => setCreatedForEmail(e.target.value)}
                  placeholder="Enter recipient's email"
                  className="mb-3"
                />
                <Button variant="primary" onClick={handleUpload} disabled={uploading}>
                  {uploading ? "Uploading..." : "Upload"}
                </Button>
                {uploading && (
                  <ProgressBar
                    animated
                    now={uploadProgress}
                    label={`${uploadProgress}%`}
                    className="mt-3"
                  />
                )}
              </Form.Group>
            </Card.Body>
          </Card>
        )}

        <h4 className="mb-4 text-primary">Accessible Documents</h4>
        <PerfectScrollbar>
          <Row className="g-4">
            {files.map((file) => (
              <Col key={file.id} md={6} lg={4}>
                <Card className="shadow-sm">
                  <Card.Body>
                    <h5>{file.fileName}</h5>
                    <p>Uploaded For: {file.createdFor}</p>
                    <p>Year: {file.year}</p>
                    <a href={file.url} target="_blank" rel="noopener noreferrer">
                      <Button variant="primary">Download</Button>
                    </a>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </PerfectScrollbar>
      </Container>
    </React.Fragment>
  );
}
